import React, { useState, useEffect, useRef, useCallback } from 'react';
import './GameStyles.css';

const SwitchGame = () => {
  const [level, setLevel] = useState(1);
  const [time, setTime] = useState(30);
  const [gameStarted, setGameStarted] = useState(false);
  const [musicEnabled, setMusicEnabled] = useState(false);
  const [gamePaused, setGamePaused] = useState(false);
  const [difficulty, setDifficulty] = useState('easy'); // easy, medium, hard
  const [consecutiveCorrect, setConsecutiveCorrect] = useState(0);
  const [consecutiveWrong, setConsecutiveWrong] = useState(0);

  // Game state
  const [topSymbols, setTopSymbols] = useState([]);
  const [bottomSymbols, setBottomSymbols] = useState([]);
  const [layers, setLayers] = useState([]); // Array of layer objects
  const [, setCurrentLayerIndex] = useState(0);
  const [feedback, setFeedback] = useState(null);

  const timerRef = useRef(null);
  const audioRef = useRef(null);

  // Symbol definitions with colors
  const symbols = [
    { shape: 'square', color: '#e74c3c' },      // red
    { shape: 'circle', color: '#27ae60' },      // green
    { shape: 'triangle', color: '#f39c12' },    // orange
    { shape: 'cross', color: '#3498db' },       // blue
    { shape: 'pentagon', color: '#9b59b6' },    // purple
    { shape: 'hexagon', color: '#e67e22' },     // dark orange
  ];

  const handleTimerEnd = useCallback(() => {
    alert(`Time's up! Moving to next level.`);
    setLevel(prev => prev + 1);
  }, []);

  useEffect(() => {
    if (gameStarted && !gamePaused) {
      timerRef.current = setInterval(() => {
        setTime((prev) => {
          if (prev <= 1) {
            handleTimerEnd();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [gameStarted, gamePaused, handleTimerEnd]);

  // Generate a random permutation sequence
  const generateSequence = (complexity) => {
    let sequence;
    if (complexity === 'easy') {
      // Simple swaps: swap 2 adjacent positions
      const swaps = [
        '2134', '1324', '1243', // swap first pair
        '3214', '2314', '2143', // other simple swaps
      ];
      sequence = swaps[Math.floor(Math.random() * swaps.length)];
    } else if (complexity === 'medium') {
      // More complex: rotate or swap multiple
      const mediumSeqs = [
        '2341', '3412', '4123', // rotations
        '2143', '3214', '4321', // reverse pairs
        '1432', '3241', '2431',
      ];
      sequence = mediumSeqs[Math.floor(Math.random() * mediumSeqs.length)];
    } else {
      // Hard: complex permutations
      const hardSeqs = [
        '4312', '3142', '2413', '4231',
        '3421', '4213', '2431', '3124',
      ];
      sequence = hardSeqs[Math.floor(Math.random() * hardSeqs.length)];
    }
    return sequence;
  };

  // Apply sequence transformation to symbols
  const applySequence = (symbolArray, sequence) => {
    const result = [];
    for (let i = 0; i < sequence.length; i++) {
      const sourceIndex = parseInt(sequence[i]) - 1; // Convert to 0-based index
      result.push(symbolArray[sourceIndex]);
    }
    return result;
  };

  // Generate wrong sequences that are different from correct one
  const generateWrongSequences = (correctSeq, count) => {
    const allSequences = [
      '1234', '1243', '1324', '1342', '1423', '1432',
      '2134', '2143', '2314', '2341', '2413', '2431',
      '3124', '3142', '3214', '3241', '3412', '3421',
      '4123', '4132', '4213', '4231', '4312', '4321',
    ];

    const wrongSeqs = allSequences.filter(seq => seq !== correctSeq);
    const selected = [];

    for (let i = 0; i < count; i++) {
      const randomIndex = Math.floor(Math.random() * wrongSeqs.length);
      selected.push(wrongSeqs[randomIndex]);
      wrongSeqs.splice(randomIndex, 1);
    }

    return selected;
  };

  const shuffle = (array) => {
    const arr = [...array];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };

  const createLevel = () => {
    // Select 4 random symbols
    const shuffledSymbols = shuffle([...symbols]);
    const selectedSymbols = shuffledSymbols.slice(0, 4);

    setTopSymbols(selectedSymbols);

    // Determine number of layers based on difficulty
    let numLayers;
    let preSelectedLayerIndex = -1;

    if (difficulty === 'easy') {
      numLayers = 1;
    } else if (difficulty === 'medium') {
      numLayers = 2;
    } else {
      numLayers = 3;
      // For hard mode, pre-select one random layer (usually middle one)
      preSelectedLayerIndex = 1; // Middle layer is pre-selected
    }

    // Create layers with transformations
    const layersData = [];
    let currentSymbols = selectedSymbols;

    for (let i = 0; i < numLayers; i++) {
      const correctSeq = generateSequence(difficulty);
      const wrongSeqs = generateWrongSequences(correctSeq, 2);
      const options = shuffle([correctSeq, ...wrongSeqs]);

      const isPreSelected = i === preSelectedLayerIndex;

      layersData.push({
        sequenceOptions: options,
        correctSequence: correctSeq,
        selectedSequence: isPreSelected ? correctSeq : null,
        isPreSelected: isPreSelected,
      });

      // Apply transformation to get next level symbols
      currentSymbols = applySequence(currentSymbols, correctSeq);
    }

    setBottomSymbols(currentSymbols);
    setLayers(layersData);
    setCurrentLayerIndex(0);
    setFeedback(null);
    setTime(30);
  };

  const handleStartGame = () => {
    setGameStarted(true);
    setLevel(1);
    setDifficulty('easy');
    setConsecutiveCorrect(0);
    setConsecutiveWrong(0);
    createLevel();
  };


  const handleSequenceClick = (layerIndex, sequence) => {
    if (feedback) return; // Prevent multiple clicks during feedback

    // Update the selected sequence for this layer
    const updatedLayers = [...layers];
    updatedLayers[layerIndex].selectedSequence = sequence;
    setLayers(updatedLayers);

    // Check if all non-pre-selected layers have been selected
    const allLayersSelected = updatedLayers.every(
      (layer) => layer.selectedSequence !== null
    );

    if (allLayersSelected) {
      // Validate all selections
      const allCorrect = updatedLayers.every(
        (layer) => layer.selectedSequence === layer.correctSequence
      );

      if (allCorrect) {
        setFeedback('correct');
        setConsecutiveCorrect(prev => prev + 1);
        setConsecutiveWrong(0);

        // Adjust difficulty based on consecutive correct answers
        setTimeout(() => {
          if (consecutiveCorrect + 1 >= 3 && difficulty === 'easy') {
            setDifficulty('medium');
            setConsecutiveCorrect(0);
          } else if (consecutiveCorrect + 1 >= 3 && difficulty === 'medium') {
            setDifficulty('hard');
            setConsecutiveCorrect(0);
          }

          setFeedback(null);
          setLevel(prev => prev + 1);
          createLevel();
        }, 1500);
      } else {
        setFeedback('wrong');
        setConsecutiveWrong(prev => prev + 1);
        setConsecutiveCorrect(0);

        // Adjust difficulty based on consecutive wrong answers
        setTimeout(() => {
          if (consecutiveWrong + 1 >= 2 && difficulty === 'hard') {
            setDifficulty('medium');
            setConsecutiveWrong(0);
          } else if (consecutiveWrong + 1 >= 2 && difficulty === 'medium') {
            setDifficulty('easy');
            setConsecutiveWrong(0);
          }

          setFeedback(null);
          setLevel(prev => prev + 1);
          createLevel();
        }, 1500);
      }
    }
  };

  const togglePause = () => {
    setGamePaused(prev => !prev);
  };

  const toggleMusic = () => {
    setMusicEnabled(prev => !prev);
    if (audioRef.current) {
      if (musicEnabled) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
    }
  };

  // Render symbol shapes
  const renderSymbol = (symbol, size = 60) => {
    const { shape, color } = symbol;

    const shapeStyle = {
      width: `${size}px`,
      height: `${size}px`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    };

    switch (shape) {
      case 'square':
        return (
          <div style={shapeStyle}>
            <div style={{
              width: `${size * 0.7}px`,
              height: `${size * 0.7}px`,
              backgroundColor: color,
              borderRadius: '4px',
            }} />
          </div>
        );

      case 'circle':
        return (
          <div style={shapeStyle}>
            <div style={{
              width: `${size * 0.7}px`,
              height: `${size * 0.7}px`,
              backgroundColor: color,
              borderRadius: '50%',
            }} />
          </div>
        );

      case 'triangle':
        return (
          <div style={shapeStyle}>
            <div style={{
              width: 0,
              height: 0,
              borderLeft: `${size * 0.35}px solid transparent`,
              borderRight: `${size * 0.35}px solid transparent`,
              borderBottom: `${size * 0.6}px solid ${color}`,
            }} />
          </div>
        );

      case 'cross':
        return (
          <div style={shapeStyle}>
            <div style={{
              position: 'relative',
              width: `${size * 0.7}px`,
              height: `${size * 0.7}px`,
            }}>
              <div style={{
                position: 'absolute',
                width: '100%',
                height: '25%',
                backgroundColor: color,
                top: '37.5%',
              }} />
              <div style={{
                position: 'absolute',
                width: '25%',
                height: '100%',
                backgroundColor: color,
                left: '37.5%',
              }} />
            </div>
          </div>
        );

      case 'pentagon':
        return (
          <div style={shapeStyle}>
            <svg width={size * 0.7} height={size * 0.7} viewBox="0 0 100 100">
              <polygon
                points="50,10 90,35 75,85 25,85 10,35"
                fill={color}
              />
            </svg>
          </div>
        );

      case 'hexagon':
        return (
          <div style={shapeStyle}>
            <svg width={size * 0.7} height={size * 0.7} viewBox="0 0 100 100">
              <polygon
                points="50,5 90,27.5 90,72.5 50,95 10,72.5 10,27.5"
                fill={color}
              />
            </svg>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="game-container switch-game">
      <audio ref={audioRef} loop>
        <source src="/assets/switch/audio/audio.mp3" type="audio/mpeg" />
      </audio>

      {!gameStarted ? (
        <div className="rules-container">
          <h1 className="game-title">Switch Challenge</h1>
          <div className="rules-content">
            <p>
              Observe the symbols displayed on the top and bottom of the screen. Your task is to identify
              which number sequence was used to rearrange the top symbols to create the bottom arrangement.
            </p>
            <h3>How to Play</h3>
            <ul>
              <li>Study the top row of 4 symbols (original order)</li>
              <li>Study the bottom row of 4 symbols (final rearranged order)</li>
              <li>Select the correct number sequences from the layers in between</li>
              <li>Example: sequence "2341" means position 1 takes symbol from position 2, position 2 takes from position 3, etc.</li>
              <li><strong>Easy:</strong> 1 layer - select 1 sequence</li>
              <li><strong>Medium:</strong> 2 layers - select 2 sequences (one at each layer)</li>
              <li><strong>Hard:</strong> 3 layers - 1 is pre-selected (purple), select the other 2</li>
              <li>Difficulty automatically adjusts based on your performance</li>
              <li>Time limit: 30 seconds per level</li>
            </ul>
            <button className="play-button" onClick={handleStartGame}>
              Play
            </button>
          </div>
        </div>
      ) : (
        <div className="game-play-area">
          <div className="game-header">
            <div className="control-buttons">
              <button onClick={togglePause} className="control-btn">
                {gamePaused ? '▶️' : '⏸️'}
              </button>
              <button onClick={toggleMusic} className="control-btn">
                {musicEnabled ? '🔊' : '🔇'}
              </button>
            </div>
            <div className="game-stat">Level {level}</div>
            <div className="game-stat">Difficulty: {difficulty}</div>
            <div className="game-stat">Time: {time}s</div>
            <div className="game-name">Switch Challenge</div>
          </div>

          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${(time / 30) * 100}%` }}></div>
          </div>

          <div className="game-content switch-game-content">
            {/* Top Symbols (Input) */}
            <div className="switch-section top-section">
              <div className="symbol-row">
                {topSymbols.map((symbol, index) => (
                  <div key={index} className="symbol-container">
                    {renderSymbol(symbol, 70)}
                  </div>
                ))}
              </div>
            </div>

            {/* Render all layers dynamically */}
            {layers.map((layer, layerIndex) => (
              <React.Fragment key={layerIndex}>
                {/* Funnel/Arrow Down */}
                <div className="funnel-container">
                  <svg width="120" height="60" viewBox="0 0 120 60">
                    <polygon points="20,0 100,0 80,30 40,30" fill="#5a6c7d" />
                    <polygon points="40,30 80,30 70,45 50,45" fill="#5a6c7d" />
                    <circle cx="35" cy="15" r="3" fill="white" opacity="0.6" />
                    <circle cx="50" cy="12" r="3" fill="white" opacity="0.6" />
                    <circle cx="65" cy="15" r="3" fill="white" opacity="0.6" />
                    <circle cx="80" cy="12" r="3" fill="white" opacity="0.6" />
                  </svg>
                </div>

                {/* Sequence Options for this layer */}
                <div className="switch-section sequence-section">
                  <div className="sequence-options">
                    {layer.sequenceOptions.map((sequence, seqIndex) => {
                      const isSelected = layer.selectedSequence === sequence;
                      const isPreSelected = layer.isPreSelected;

                      return (
                        <button
                          key={seqIndex}
                          className={`sequence-option ${
                            isSelected
                              ? isPreSelected
                                ? 'pre-selected'
                                : 'selected'
                              : ''
                          }`}
                          onClick={() => handleSequenceClick(layerIndex, sequence)}
                          disabled={feedback !== null || isPreSelected}
                        >
                          {sequence}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </React.Fragment>
            ))}

            {/* Final Funnel/Arrow Down to bottom symbols */}
            <div className="funnel-container">
              <svg width="120" height="60" viewBox="0 0 120 60">
                <polygon points="50,0 70,0 80,30 40,30" fill="#5a6c7d" />
                <polygon points="40,30 80,30 100,60 20,60" fill="#5a6c7d" />
                <circle cx="35" cy="45" r="3" fill="white" opacity="0.6" />
                <circle cx="50" cy="48" r="3" fill="white" opacity="0.6" />
                <circle cx="65" cy="45" r="3" fill="white" opacity="0.6" />
                <circle cx="80" cy="48" r="3" fill="white" opacity="0.6" />
              </svg>
            </div>

            {/* Bottom Symbols (Output) */}
            <div className="switch-section bottom-section">
              <div className="symbol-row">
                {bottomSymbols.map((symbol, index) => (
                  <div key={index} className="symbol-container">
                    {renderSymbol(symbol, 70)}
                  </div>
                ))}
              </div>
            </div>

            {/* Feedback Message */}
            {feedback && (
              <div className={`feedback-overlay ${feedback}`}>
                <div className="feedback-content">
                  <div className="feedback-icon">
                    {feedback === 'correct' ? (
                      <svg width="100" height="100" viewBox="0 0 100 100">
                        <circle cx="50" cy="50" r="45" fill="#48bb78" />
                        <path
                          d="M30 50 L45 65 L70 35"
                          stroke="white"
                          strokeWidth="8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          fill="none"
                        />
                      </svg>
                    ) : (
                      <svg width="100" height="100" viewBox="0 0 100 100">
                        <circle cx="50" cy="50" r="45" fill="#f56565" />
                        <path
                          d="M35 35 L65 65 M65 35 L35 65"
                          stroke="white"
                          strokeWidth="8"
                          strokeLinecap="round"
                        />
                      </svg>
                    )}
                  </div>
                  <div className="feedback-text">
                    {feedback === 'correct' ? 'Correct!' : 'Wrong!'}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SwitchGame;
